<template>
	<div
		ref="groupRef"
		class="journal-tabs"
		:class="[`is-moving-${direction}`, { 'is-animating': animating }]"
		role="group"
		aria-label="Appointments view"
	>
		<span class="journal-tabs-thumb" :style="thumbStyle" aria-hidden="true"></span>
		<button
			ref="journalRef"
			class="journal-tab"
			:class="{ active: !modelValue }"
			:aria-pressed="!modelValue"
			type="button"
			@click="choose(false)"
		>
			<AppointmentIcon name="journal" />
			<span>Journal</span>
		</button>
		<button
			ref="summaryRef"
			class="journal-tab"
			:class="{ active: modelValue }"
			:aria-pressed="modelValue"
			type="button"
			@click="choose(true)"
		>
			<AppointmentIcon name="summary" />
			<span>Summary</span>
		</button>
	</div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';

const props = defineProps({
	modelValue: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const groupRef = ref(null);
const journalRef = ref(null);
const summaryRef = ref(null);
const thumb = ref({ left: 0, right: 0 });
const animating = ref(false);
const direction = ref('right');
let observer;
let settleTimer;

const thumbStyle = computed(() => ({
	left: `${thumb.value.left}px`,
	right: `${thumb.value.right}px`,
}));

// Both edges of the thumb are positioned separately, so they can travel at slightly different times.
const measure = () => {
	const group = groupRef.value;
	const tab = props.modelValue ? summaryRef.value : journalRef.value;
	if (!group || !tab) return;
	thumb.value = {
		left: tab.offsetLeft,
		right: group.clientWidth - tab.offsetLeft - tab.offsetWidth,
	};
};

// Motion is switched on only for a click on the switch. Opening the page, restoring the view
// or layout changes move the thumb instantly.
const choose = (summary) => {
	if (summary === props.modelValue) return;
	direction.value = summary ? 'right' : 'left';
	animating.value = true;
	clearTimeout(settleTimer);
	settleTimer = setTimeout(() => {
		animating.value = false;
	}, 520);
	emit('update:modelValue', summary);
};

watch(() => props.modelValue, measure, { flush: 'post' });

onMounted(() => {
	measure();
	if (typeof ResizeObserver !== 'undefined') {
		observer = new ResizeObserver(measure);
		observer.observe(groupRef.value);
	}
});

onBeforeUnmount(() => {
	observer?.disconnect();
	clearTimeout(settleTimer);
});
</script>

<style scoped>
.journal-tabs {
	--tab-flow: cubic-bezier(0.45, 0, 0.2, 1);
	position: relative;
	display: flex;
	gap: 2px;
	padding: 2px;
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 8px;
	/* Opaque, so the corner art behind it on narrow screens doesn't show through. */
	background: var(--noc-background, #fff);
}

.journal-tabs-thumb {
	position: absolute;
	top: 2px;
	bottom: 2px;
	border-radius: 6px;
	background: var(--noc-blue-05, rgba(10, 97, 201, 0.05));
	pointer-events: none;
}

/* The leading edge moves first and the trailing edge follows, so the thumb flows
   from one tab to the other without overshooting. */
.journal-tabs.is-animating .journal-tabs-thumb {
	transition:
		left 380ms var(--tab-flow),
		right 380ms var(--tab-flow);
}

.journal-tabs.is-animating.is-moving-right .journal-tabs-thumb {
	transition-delay: 80ms, 0ms;
}

.journal-tabs.is-animating.is-moving-left .journal-tabs-thumb {
	transition-delay: 0ms, 80ms;
}

.journal-tab {
	position: relative;
	z-index: 1;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	justify-content: center;
	min-width: 96px;
	height: 30px;
	padding: 0 18px;
	border: none;
	border-radius: 6px;
	background: none;
	box-shadow: none;
	color: var(--text-color-secondary, #6b7280);
	cursor: pointer;
	white-space: nowrap;
	font: var(--ap-type-control);
	transition: background-color 150ms ease;
}

.journal-tab:not(.active):hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 3%, transparent);
}

.journal-tab.active {
	color: var(--noc-blue, #0a61c9);
	font-weight: 500;
}

.journal-tabs.is-animating .journal-tab {
	transition:
		color 180ms ease,
		background-color 150ms ease;
}

/* The label turns blue as the thumb arrives underneath it. */
.journal-tabs.is-animating .journal-tab.active {
	transition:
		color 180ms ease 120ms,
		background-color 150ms ease;
}

@media (prefers-reduced-motion: reduce) {
	.journal-tabs.is-animating .journal-tabs-thumb,
	.journal-tabs.is-animating .journal-tab,
	.journal-tabs.is-animating .journal-tab.active {
		transition: none;
	}
}
</style>
